/**
 * M-PAiSA Payment Callback Page
 * Handles redirects from M-PAiSA after payment completion
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Clock, AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { api } from '@/utils/apiClient';

export default function PaymentCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get callback parameters
        const rCode = searchParams.get('rCode');
        const transactionID = searchParams.get('transactionID');
        const requestID = searchParams.get('requestID');
        const customerPhone = searchParams.get('customerphonenumber');
        const tokenv2 = searchParams.get('tokenv2');
        const amt = searchParams.get('amt');

        // Send to backend for verification
        const response = await api.get(
          `/payments/mpaisa/callback?` + 
          `rCode=${rCode}&transactionID=${transactionID}&requestID=${requestID}` +
          `&customerphonenumber=${customerPhone}&tokenv2=${tokenv2}&amt=${amt}`
        );

        const data = response.data;
        setPaymentData(data);

        if (data.success) {
          setStatus('success');
        } else if (data.status === 'cancelled') {
          setStatus('cancelled');
        } else {
          setStatus('failed');
          setError(data.message || 'Payment failed');
        }
      } catch (err) {
        setStatus('error');
        setError(err.message);
      }
    };

    processCallback();
  }, [searchParams]);

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'cancelled':
        return <XCircle className="w-16 h-16 text-amber-500" />;
      case 'failed':
        return <XCircle className="w-16 h-16 text-red-500" />;
      case 'processing':
        return <Clock className="w-16 h-16 text-blue-500 animate-pulse" />;
      default:
        return <AlertTriangle className="w-16 h-16 text-gray-500" />;
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'success':
        return {
          title: 'Payment Successful!',
          description: 'Your order has been confirmed and is being prepared.',
          color: 'text-green-600 dark:text-green-400'
        };
      case 'cancelled':
        return {
          title: 'Payment Cancelled',
          description: 'You cancelled the payment. Your order has not been processed.',
          color: 'text-amber-600 dark:text-amber-400'
        };
      case 'failed':
        return {
          title: 'Payment Failed',
          description: error || 'There was an issue processing your payment. Please try again.',
          color: 'text-red-600 dark:text-red-400'
        };
      case 'processing':
        return {
          title: 'Processing Payment...',
          description: 'Please wait while we verify your payment.',
          color: 'text-blue-600 dark:text-blue-400'
        };
      default:
        return {
          title: 'Unknown Status',
          description: 'Unable to determine payment status.',
          color: 'text-gray-600 dark:text-gray-400'
        };
    }
  };

  const statusInfo = getStatusMessage();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {getStatusIcon()}
          </div>
          <CardTitle className={statusInfo.color}>
            {statusInfo.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-center text-muted-foreground">
            {statusInfo.description}
          </p>

          {paymentData && status === 'success' && (
            <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Transaction ID:</span>
                <span className="font-mono font-medium text-foreground">{paymentData.transaction_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Request ID:</span>
                <span className="font-mono font-medium text-foreground">{paymentData.request_id}</span>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                Your order will be ready for pickup in approximately
              </p>
              <p className="text-2xl font-bold text-foreground">15 minutes</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => navigate('/')}
              className="flex-1"
              variant={status === 'success' ? 'default' : 'outline'}
            >
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            
            {(status === 'failed' || status === 'cancelled') && (
              <Button 
                onClick={() => navigate('/menu')}
                className="flex-1"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
          </div>

          {status === 'success' && (
            <p className="text-xs text-center text-muted-foreground">
              A confirmation has been sent to your phone. Thank you for ordering with Bulaccino Cafe!
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
